/**
 * E-mail do cliente: um valor para EXIBIR, outro para ENVIAR.
 *
 * Por que este util existe: `app_clientes` tem duas colunas com papéis
 * diferentes e nomes que não denunciam isso. `login` é a CREDENCIAL de acesso
 * do cliente — é por ela que a autenticação e a recuperação de senha acham a
 * conta; `email` é o e-mail de CONTATO. O front tratou as duas como sinônimos
 * por anos, e daí vem o defeito visível: o autocomplete do Consolidador
 * renderizava `login` cru, então um lead criado sem e-mail — que o CRM grava
 * como `lead-<slug>-<hex8>@lead.com` nos DOIS campos — continuava aparecendo
 * para a operação como `lead-lucia-a1b2c3d4@lead.com` mesmo depois de alguém
 * corrigir o e-mail de verdade.
 *
 * Por que não basta `item.email || item.login`: parte da base tem `login` que
 * não é e-mail nenhum e está EM USO como credencial — código da corretora
 * (`003529971`), `fee_fixo_*`, `bi_auto_*`, `cli_*` e endereços no domínio
 * sintético `@auto.aawz.com.br`. Esses valores precisam ser EXIBIDOS (são o
 * único identificador que a operação reconhece naquele cliente), mas mandar
 * e-mail ou convite para eles é bounce garantido. Por isso são duas funções, e
 * não uma: `emailDoCliente` para tela, `emailEnviavelDoCliente` para envio.
 *
 * O critério de "não é endereço real" é DE PROPÓSITO o mesmo do backend
 * (`aawz_base/usecases/cliente_identidade.py`, `classificar_login`). Se as
 * duas pontas divergirem, o front oferece "enviar e-mail" para um cliente que
 * o backend recusa — ou esconde a ação de um cliente que funcionaria. Mudou
 * lá, muda aqui.
 *
 * São TRÊS cópias, sem pacote compartilhado: esta, a do Interno
 * (`aawz-web-interno/src/utils/clienteEmail.js`) e a do backend. A duplicação
 * está declarada em `spec-001-.../design/components-reused.md` e o contrato de
 * comportamento em `spec-001-.../contracts/ui-components.md` §1 — mexeu em uma,
 * mexe nas outras duas e replique o caso de teste nas duas suítes.
 */

// Domínio dos placeholders que o CRM emite para lead sem e-mail. Não é um
// domínio nosso e ninguém recebe mensagem nele.
const DOMINIO_PLACEHOLDER_LEAD = 'lead.com'

// Domínio colado nos logins gerados por fee_fixo e bi_auto quando o cliente
// entrou na base sem e-mail. Parece e-mail, mas é identificador interno.
const DOMINIO_SINTETICO_AUTO = 'auto.aawz.com.br'

// Prefixos dos logins gerados por máquina. `cli_` cobre `cli_<id>` e o
// `cli_pending` que o proxy usa quando o insert chega sem login.
const PREFIXOS_LOGIN_LEGADO = ['fee_fixo_', 'bi_auto_', 'cli_']

// Formato aceito para e-mail — o mesmo `_PADRAO_EMAIL` do backend. Exige TLD
// (`.com`, `.br`) de propósito: `a@b` passa em validação ingênua de "tem @ e
// ponto" e só falha lá na hora do envio, sem ninguém saber por quê.
//
// NÃO reaproveita `checkEmail` (`@utils/Format`), e a duplicação é deliberada:
// `checkEmail` valida o que o usuário DIGITA num formulário e aceita mais do
// que o backend — local-part entre aspas (`"a b"@d.com`) e IP entre colchetes
// (`a@[10.0.0.1]`). Aqui a pergunta é outra: "o backend aceitaria este endereço
// como destinatário?", e só a regex dele responde isso. Unificar as duas numa
// só quebra uma das pontas — o formulário passa a recusar o que aceitava, ou o
// front volta a oferecer envio para endereço que o backend recusa.
//
// A regex do backend é uma só; aqui ela está partida em duas metades, com o
// MESMO conjunto de endereços aceitos. A partição é exata porque nenhuma das
// duas metades aceita `@`: qualquer endereço que o `_PADRAO_EMAIL` aceita tem
// exatamente um `@`, e é nele que `temFormatoDeEmail` corta. (Partida só pela
// forma — a regex única estourava o limite de complexidade do SonarQube.) A
// equivalência é provada em `clienteEmail.unit.js` contra a regex antiga.
//
// Parte local: átomos separados por ponto, sem ponto no início, no fim ou
// duplicado.
const PADRAO_PARTE_LOCAL = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/

// Domínio: rótulos que não começam nem terminam em hífen, e TLD só de letras
// com 2+ caracteres.
const PADRAO_DOMINIO =
  /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/

/**
 * Diz se o valor tem o formato que o `_PADRAO_EMAIL` do backend aceita.
 *
 * @param {String} valorLimpo - saída de `limpar`.
 * @returns {Boolean}
 */
function temFormatoDeEmail(valorLimpo) {
  const posicaoArroba = valorLimpo.indexOf('@')
  const temUmArrobaSo =
    posicaoArroba > 0 && posicaoArroba === valorLimpo.lastIndexOf('@')
  if (!temUmArrobaSo) return false

  const parteLocal = valorLimpo.slice(0, posicaoArroba)
  const dominio = valorLimpo.slice(posicaoArroba + 1)
  return PADRAO_PARTE_LOCAL.test(parteLocal) && PADRAO_DOMINIO.test(dominio)
}

/**
 * Tira os espaços das pontas de um valor cru vindo da API, SEM mexer na caixa.
 *
 * É a forma de EXIBIÇÃO: o login legado `CLI_42` aparece como está no banco,
 * porque é assim que a operação o procura com os olhos (review SHOULD-7 do
 * PR #2167). Quem COMPARA o valor exibido com texto digitado baixa a caixa
 * dos dois lados no próprio filtro — ver `customFilter` do client-search.
 *
 * @param {*} valor - valor cru (`null`/`undefined`/número são tolerados).
 * @returns {String} valor sem espaço nas pontas; `''` quando a entrada é vazia.
 */
function aparar(valor) {
  if (valor === null || valor === undefined) return ''
  return String(valor).trim()
}

/**
 * Normaliza um valor cru vindo da API para CLASSIFICAR e ENVIAR: sem espaço
 * nas pontas e em caixa baixa, como `normalizar_email` no backend.
 *
 * A classificação é case-insensitive porque o mesmo endereço chega em caixas
 * diferentes dependendo de quem gravou (modal do front, CRM, importação de
 * planilha, backfill). Não vale para exibição — lá é `aparar`.
 *
 * @param {*} valor - valor cru (`null`/`undefined`/número são tolerados).
 * @returns {String} valor normalizado; `''` quando a entrada é vazia.
 */
function limpar(valor) {
  return aparar(valor).toLowerCase()
}

/**
 * Extrai o domínio (o que vem depois do último `@`) de um valor já limpo.
 *
 * @param {String} valorLimpo - saída de `limpar`.
 * @returns {String} domínio, ou `''` quando não há `@`.
 */
function dominioDe(valorLimpo) {
  const posicaoArroba = valorLimpo.lastIndexOf('@')
  if (posicaoArroba === -1) return ''
  return valorLimpo.slice(posicaoArroba + 1)
}

/**
 * Diz se o valor é um endereço para o qual dá para mandar mensagem.
 *
 * A ordem das checagens importa e é a mesma do backend:
 *   1. domínio ANTES de formato — `fee_fixo_1@auto.aawz.com.br` é um e-mail
 *      sintaticamente perfeito, e só o domínio revela que é interno;
 *   2. `lead.com` é reconhecido pelo DOMÍNIO, sem exigir o prefixo `lead-` —
 *      é o que mantém `lead.silva@empresa.com` (sobrenome começando com
 *      "lead", domínio real) classificado como endereço de verdade;
 *   3. prefixo de máquina depois, para pegar `bi_auto_83_1` e `cli_pending`,
 *      que não têm `@` nenhum;
 *   4. o que sobra e não casa com formato de e-mail (código da corretora
 *      `003529971`) nunca é endereço real.
 *
 * @param {String} valorLimpo - saída de `limpar`.
 * @returns {Boolean}
 */
function ehEnderecoReal(valorLimpo) {
  if (!valorLimpo) return false

  const dominio = dominioDe(valorLimpo)
  if (dominio === DOMINIO_PLACEHOLDER_LEAD) return false
  if (dominio === DOMINIO_SINTETICO_AUTO) return false

  const ehLoginDeMaquina = PREFIXOS_LOGIN_LEGADO.some((prefixo) => {
    return valorLimpo.startsWith(prefixo)
  })
  if (ehLoginDeMaquina) return false

  return temFormatoDeEmail(valorLimpo)
}

/**
 * E-mail do cliente para EXIBIR em tela.
 *
 * Usa o `email` de contato quando ele existe e cai no `login` só quando não
 * existe — é a ordem inversa da que o Consolidador usava, e é ela que faz o
 * e-mail corrigido aparecer no lugar do placeholder de lead. O `login` segue
 * como fallback (e não é filtrado por formato) porque, para cliente de base
 * legada, o código da corretora é o único identificador que a operação
 * reconhece; escondê-lo deixaria a linha em branco.
 *
 * O valor sai só com `trim()` — a caixa é a do banco (`CLI_42` continua
 * `CLI_42`). Por isso quem usa o retorno para BUSCAR precisa baixar a caixa
 * dos dois lados na comparação; já `emailEnviavelDoCliente` devolve em caixa
 * baixa, porque ali o valor é destinatário, não texto de tela.
 *
 * Nunca devolve `undefined`/`null`: quem chama renderiza direto no template.
 *
 * @param {Object} item - cliente da lista (`user/allClients` ou equivalente).
 * @returns {String} e-mail, login (sem espaço nas pontas, caixa preservada)
 *   ou `''`.
 */
export function emailDoCliente(item) {
  if (!item) return ''

  const email = aparar(item.email)
  if (email) return email

  return aparar(item.login)
}

/**
 * E-mail do cliente para ENVIAR e-mail/convite.
 *
 * Devolve `''` quando nenhum dos dois campos é um endereço para o qual dá para
 * mandar mensagem (placeholder `@lead.com`, domínio sintético
 * `@auto.aawz.com.br`, login gerado por máquina, código da corretora). Quem
 * chama deve tratar `''` como "cliente sem e-mail" — mensagem enviada para
 * placeholder volta como bounce e ninguém fica sabendo.
 *
 * Por que NÃO parte de `emailDoCliente`: aquela função é de EXIBIÇÃO e para no
 * primeiro campo preenchido. Com `{email: 'lead-x-1@lead.com', login:
 * 'real@d.com'}` — cadastro antigo em que o endereço bom ficou na CREDENCIAL e
 * o `email` ficou no placeholder — ela devolveria o placeholder, e o cliente
 * sumiria da lista de destinatários tendo e-mail válido. Aqui cada campo é
 * testado por vez, na ordem `email` → `login`, e o primeiro enviável vence.
 *
 * É a mesma semântica das outras duas cópias, de propósito: o backend em
 * `aawz_base/api/db_dir/professional/api_app_link_manager.py`
 * (`resolver_email_destinatario`) percorre os candidatos e aceita o primeiro
 * que `classificar_login` marca como `LoginKind.EMAIL`, e o Interno em
 * `aawz-web-interno/src/utils/clienteEmail.js` faz o mesmo. Mudou aqui, muda
 * nas outras duas.
 *
 * @param {Object} item - cliente da lista (`user/allClients` ou equivalente).
 * @returns {String} endereço enviável ou `''`.
 */
export function emailEnviavelDoCliente(item) {
  if (!item) return ''

  const email = limpar(item.email)
  if (ehEnderecoReal(email)) return email

  const login = limpar(item.login)
  if (ehEnderecoReal(login)) return login

  return ''
}
